<template>
  <div class="terms">
    <div class="header mt-3 h1">
      RWA.capital
    </div>
    <div class="header mt-2 h1">
      Complaints Handling Procedure
    </div>
    <div class="h5 mt-5 ms-4 me-4">
      <p><strong>Version</strong> 1.0</p>
      <p><strong>Effective date:</strong> [Feb 10, 2026]</p>

      <p class="mt-3">
        This document sets out the procedure for submitting and handling complaints from clients of RWA.capital (the “Company”).
        The Company shall maintain written procedures, ensure fair, consistent and timely handling of complaints, as well as shall document the outcomes.
      </p>

      <h3 class="mt-4">
        1. Purpose and Principles
      </h3>
      <p>
        The purposes of this procedure are to:
      </p>
      <ul>
        <li>Offer a clear and accessible way to submit a complaint</li>
        <li>Handle complaints objectively and with no undue delay</li>
        <li>Offer, where necessary, reasonable remedial measures (including redress)</li>
      </ul>
      <p>
        The Company aims to ensure that the handling process is transparent and that decisions are reasoned and clear to the client.
      </p>

      <h3 class="mt-4">
        2. What Is Considered a Complaint
      </h3>
      <p>
        A complaint is an expression of dissatisfaction with an act or inaction of the Company (or persons acting on its behalf) in connection with the provision of services, where the client believes that they have suffered or may suffer certain adverse effects (e.g., financial loss, material inconvenience, or other harm).
      </p>
      <p>
        A request for information, a routine support enquiry, or a technical issue shall not always constitute a complaint.
        However, in the event a message requests remediation, compensation, or implies a material issue, the Company shall treat it as a complaint.
      </p>

      <h3 class="mt-4">
        3. How to Submit a Complaint
      </h3>
      <p>
        Complaints are accepted by email only: <a href="mailto: complaints@RWA.capital">complaints@RWA.capital</a>
        To help us investigate promptly, please make sure your email contains the following:
      </p>
      <ul>
        <li>Your full name and contact details</li>
        <li>Description of the situation (what happened, when, and what consequences arose)</li>
        <li>Identifiers to help locate the respective transaction/investment/deal (if applicable)</li>
        <li>What resolution you would consider acceptable</li>
        <li>Where available, supporting documents or screenshots</li>
      </ul>

      <h3 class="mt-4">
        4. Complaints Handling Process
      </h3>
      <p>
        The Company shall appoint a person in charge of handling complaints, and may involve relevant teams to investigate the circumstances.
      </p>

      <h3 class="mt-4">
        4.1 Acknowledgement
      </h3>
      <p>The Company shall send a written acknowledgement of the complaint receipt as well as shall provide:</p>
      <ul>
        <li>Unique complaint reference number</li>
        <li>Contact details of the person in charge of handling (via <a href="mailto: complaints@RWA.capital">complaints@RWA.capital</a>);</li>
        <li>A brief description of further steps and approximate timelines</li>
      </ul>
      <p>
        As a rule, the acknowledgement shall be sent within 7 calendar days following the date the complaint is received.
      </p>

      <h3 class="mt-4">
        4.2 Review and Information Requests
      </h3>
      <p>
        The Company shall review the information submitted, request additional data where necessary, and carry out an internal investigation.
        The client is expected to cooperate in good faith by offering requested information within a reasonable timeframe.
      </p>

      <h3 class="mt-4">
        4.3  Status Updates
      </h3>
      <p>
        The Company shall, where necessary, keep the client informed concerning the progress of the complaint handling.
        In case the complaint cannot be closed within 30 calendar days, the Company shall provide a status update explaining the reasons for the delay and the expected completion date.
      </p>

      <h3 class="mt-4">
        4.4  Final Response and Resolution
      </h3>
      <p>
        Upon completion of the investigation, the Company shall send the client a written final response setting out:
      </p>
      <ul>
        <li>The outcome of the review and the reasoning behind the decision</li>
        <li>Where a breach is identified, remedial measures and/or the terms of redress</li>
        <li>The method and timeframe for implementing the respectively agreed measures</li>
      </ul>
      <p>
        The Company shall aim to close most complaints within 60 calendar days from the date of receipt.
        For complex cases, timelines may be extended of which the client shall be notified in advance.
      </p>

      <h3 class="mt-4">
        5. Independence and Conflict of Interest
      </h3>
      <p>
        Complaints shall be handled objectively. Where possible, the complaint shall be reviewed by an employee not involved in the matters giving rise to the complaint.
        Where a conflict of interest exists, the Company shall take steps to mitigate it (e.g., by reallocating the complaint to another responsible person).
      </p>

      <h3 class="mt-4">
        6. Possible Outcomes
      </h3>
      <p>
        Depending on the circumstances, the Company may:
      </p>
      <ul>
        <li>Offer explanations and/or correct an error (if identified)</li>
        <li>Implement corrective measures to prevent recurrence</li>
        <li>Offer redress (monetary or otherwise) where justified</li>
        <li>Reject the complaint where no breach is established, providing respective reasons</li>
      </ul>

      <h3 class="mt-4">
        7. Escalation
      </h3>
      <p>
        In the event you are not satisfied with the Company’s final response, you may employ any available avenues to protect your rights, including submitting a complaint to the regulator (DFSA) subject to the DFSA consumer complaints process.
        Information on how to submit a complaint is available on the DFSA official resource (Consumer Complaints section).
      </p>

      <h3 class="mt-4">
        8. Recordkeeping and Retention
      </h3>
      <p>
        The Company shall maintain a complaint register as well as shall retain respective records related to complaints (including correspondence, investigation materials, and final decisions) for at least 6 years, or longer if required by applicable law or regulatory requirements.
        The Company shall analyze complaint drivers and use the findings to improve processes and enhance risk controls.
      </p>

      <h3 class="mt-4">
        9. Confidentiality and Personal Data
      </h3>
      <p>
        Information about a complaint and any supporting materials shall be used solely for handling the complaint, implementing decisions, and meeting regulatory obligations.
        Access to the data shall be limited to individuals who need it for the purpose of handling the complaint.
        Personal data shall be processed following applicable requirements and the Company’s internal policies.
      </p>

      <h3 class="mt-4">
        10. Availability and Updates
      </h3>
      <p>
        The Company may update this procedure from time to time.
        The effective version shall be published on the Company’s website.
        Upon a client’s request, a copy of the procedure shall be provided free of charge (via <a href="mailto: complaints@RWA.capital">complaints@RWA.capital</a>).
      </p>
      <p>
        Note: This procedure has been prepared based on the DFSA Rulebook requirements (GEN, Section 9.2 Complaints Handling) and the DFSA practical guidance.
      </p>
    </div>
  </div>
</template>

<script setup>
import { checkAccess, scope } from "@/help/accessData";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});
</script>

<style scoped lang="scss">
ul {
  list-style-type: disc;
}
.terms {
  background-color: var(--main-color);
  padding-bottom: 10px;

  .header {
    display: flex;
    justify-content: center;
  }
}
a {
  color: var(--color-green);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>